/**
 * Lab Status Panel
 * Main component for instructor lab infrastructure management
 * Shows status, progress, and actions for provisioning/terminating labs
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { ProvisionLabsDialog } from './ProvisionLabsDialog';
import { ReduceLabsDialog } from './ReduceLabsDialog';
import {
  Plus,
  Minus,
  Trash2,
  RefreshCw,
  Server,
  Users,
  Clock,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { LabStatusResponse } from '../../hooks/useLabs';
import type { Class, LabInstanceStatus } from '@cloak-next/shared';

interface LabStatusPanelProps {
  classData: Class;
  labStatus: LabStatusResponse | null;
  currentEnrollments: number;
  loading: boolean;
  onPrime: (count: number) => Promise<void>;
  onReduce: (count: number) => Promise<void>;
  onTerminateAll: () => Promise<void>;
  onRefresh: () => void;
}

/**
 * Get status badge variant and color
 */
function getStatusBadge(status: LabInstanceStatus, studentEmail?: string): {
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  className: string;
  label: string;
} {
  const isUnassigned = !studentEmail || studentEmail === 'UNASSIGNED';

  switch (status) {
    case 'ready':
      if (isUnassigned) {
        return {
          variant: 'secondary',
          className: 'bg-blue-100 text-blue-800 border-blue-200',
          label: 'Primed',
        };
      }
      return {
        variant: 'default',
        className: 'bg-green-100 text-green-800 border-green-200',
        label: 'Ready',
      };
    case 'primed':
      return {
        variant: 'secondary',
        className: 'bg-blue-100 text-blue-800 border-blue-200',
        label: 'Primed',
      };
    case 'provisioning':
      return {
        variant: 'outline',
        className: 'bg-amber-50 text-amber-800 border-amber-200',
        label: 'Provisioning',
      };
    case 'failed':
      return {
        variant: 'destructive',
        className: 'bg-red-100 text-red-800 border-red-200',
        label: 'Failed',
      };
    case 'terminating':
      return {
        variant: 'outline',
        className: 'bg-orange-50 text-orange-800 border-orange-200',
        label: 'Terminating',
      };
    case 'terminated':
      return {
        variant: 'outline',
        className: 'bg-gray-100 text-gray-600 border-gray-200',
        label: 'Terminated',
      };
    default:
      return {
        variant: 'outline',
        className: '',
        label: status,
      };
  }
}

export function LabStatusPanel({
  classData,
  labStatus,
  currentEnrollments,
  loading,
  onPrime,
  onReduce,
  onTerminateAll,
  onRefresh,
}: LabStatusPanelProps) {
  const [isProvisionDialogOpen, setIsProvisionDialogOpen] = useState(false);
  const [isReduceDialogOpen, setIsReduceDialogOpen] = useState(false);
  const [isTerminateDialogOpen, setIsTerminateDialogOpen] = useState(false);
  const [isTerminating, setIsTerminating] = useState(false);

  const handleTerminateAll = async () => {
    setIsTerminating(true);
    try {
      await onTerminateAll();
      setIsTerminateDialogOpen(false);
    } finally {
      setIsTerminating(false);
    }
  };

  // Calculate stats
  const maxStudents = labStatus?.maxStudents || classData.maxStudents;
  const totalInstances = labStatus?.totalInstances || 0;
  const assignedCount = labStatus?.activeInstances || 0;
  const unassignedCount = labStatus?.primedInstances || 0;
  const provisioningCount = labStatus?.provisioningInstances || 0;
  const failedCount = labStatus?.failedInstances || 0;

  // Calculate progress
  const readyCount = assignedCount + unassignedCount;
  const totalInProgress = provisioningCount + readyCount;
  const progressPercent = totalInProgress > 0 ? (readyCount / totalInProgress) * 100 : 0;

  // Sort instances: provisioning first, then by status, then by provisioned date
  const sortedInstances = [...(labStatus?.instances || [])].sort((a, b) => {
    // Provisioning instances first
    if (a.status === 'provisioning' && b.status !== 'provisioning') return -1;
    if (b.status === 'provisioning' && a.status !== 'provisioning') return 1;
    // Then by status
    const statusOrder: Record<LabInstanceStatus, number> = {
      provisioning: 0,
      ready: 1,
      primed: 2,
      failed: 3,
      terminating: 4,
      terminated: 5,
    };
    if (statusOrder[a.status] !== statusOrder[b.status]) {
      return statusOrder[a.status] - statusOrder[b.status];
    }
    // Then by provisioned date (newest first)
    return new Date(b.provisionedAt).getTime() - new Date(a.provisionedAt).getTime();
  });

  const hasInstances = totalInstances > 0;
  const hasActiveInstances = readyCount > 0 || provisioningCount > 0;
  const canProvision = classData.status !== 'draft' && classData.status !== 'ended';
  const canReduce = unassignedCount > 0;
  const canTerminate = hasActiveInstances;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-xl font-bold">Lab Infrastructure</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-lg border p-3 text-center">
              <div className="text-2xl font-bold">{maxStudents}</div>
              <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                <Users className="h-3 w-3" />
                Ceiling
              </div>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <div className="text-2xl font-bold">{totalInstances}</div>
              <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                <Server className="h-3 w-3" />
                Provisioned
              </div>
            </div>
            <div className="rounded-lg border p-3 text-center bg-green-50">
              <div className="text-2xl font-bold text-green-700">{assignedCount}</div>
              <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                Assigned
              </div>
            </div>
            <div className="rounded-lg border p-3 text-center bg-blue-50">
              <div className="text-2xl font-bold text-blue-700">{unassignedCount}</div>
              <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                Unassigned
              </div>
            </div>
          </div>

          {/* Provisioning Progress */}
          {provisioningCount > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Provisioning in progress
                </span>
                <span className="font-medium">
                  {readyCount} of {totalInProgress} ready
                </span>
              </div>
              <Progress value={progressPercent} className="h-2" />
              <p className="text-xs text-muted-foreground">
                Status updates automatically. Click Refresh to check progress.
              </p>
            </div>
          )}

          {/* Failed instances warning */}
          {failedCount > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <AlertCircle className="h-4 w-4" />
              <span>
                {failedCount} instance{failedCount !== 1 ? 's' : ''} failed to provision.
                Check CloudFormation for details.
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={() => setIsProvisionDialogOpen(true)}
              disabled={!canProvision || loading}
            >
              <Plus className="h-4 w-4 mr-1" />
              Provision More
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsReduceDialogOpen(true)}
              disabled={!canReduce || loading}
            >
              <Minus className="h-4 w-4 mr-1" />
              Reduce Unassigned
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setIsTerminateDialogOpen(true)}
              disabled={!canTerminate || loading}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Terminate All
            </Button>
          </div>

          {/* Instance Details Table */}
          {hasInstances ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr className="text-left">
                    <th className="pb-2 font-medium">Instance</th>
                    <th className="pb-2 font-medium">Status</th>
                    <th className="pb-2 font-medium">Student</th>
                    <th className="pb-2 font-medium">Public IP</th>
                    <th className="pb-2 font-medium">Provisioned</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedInstances.map((instance) => {
                    const statusBadge = getStatusBadge(instance.status, instance.studentEmail);
                    const isUnassigned = !instance.studentEmail || instance.studentEmail === 'UNASSIGNED';

                    return (
                      <tr key={instance.instanceId} className="border-b last:border-0">
                        <td className="py-3">
                          <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                            {instance.instanceId}
                          </code>
                        </td>
                        <td className="py-3">
                          <Badge
                            variant={statusBadge.variant}
                            className={statusBadge.className}
                          >
                            {instance.status === 'provisioning' && (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            )}
                            {statusBadge.label}
                          </Badge>
                        </td>
                        <td className="py-3">
                          {isUnassigned ? (
                            <span className="text-muted-foreground">-</span>
                          ) : (
                            instance.studentEmail
                          )}
                        </td>
                        <td className="py-3">
                          {instance.instancePublicData?.[0]?.publicIp ? (
                            <code className="text-xs font-mono">{instance.instancePublicData[0].publicIp}</code>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-3 text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {instance.provisionedAt && !isNaN(new Date(instance.provisionedAt).getTime())
                              ? formatDistanceToNow(new Date(instance.provisionedAt), {
                                  addSuffix: true,
                                })
                              : '-'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Server className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No lab instances provisioned yet.</p>
              {canProvision && (
                <p className="text-sm">
                  Click "Provision More" to create lab environments for students.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Provision Dialog */}
      <ProvisionLabsDialog
        open={isProvisionDialogOpen}
        onOpenChange={setIsProvisionDialogOpen}
        className={classData.className}
        currentEnrollments={currentEnrollments}
        currentProvisionedCount={totalInstances}
        maxStudents={maxStudents}
        onPrime={onPrime}
      />

      {/* Reduce Dialog */}
      <ReduceLabsDialog
        open={isReduceDialogOpen}
        onOpenChange={setIsReduceDialogOpen}
        className={classData.className}
        unassignedCount={unassignedCount}
        onReduce={onReduce}
      />

      {/* Terminate All Confirmation */}
      <AlertDialog open={isTerminateDialogOpen} onOpenChange={setIsTerminateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Terminate All Labs?</AlertDialogTitle>
            <AlertDialogDescription>
              This will terminate <strong>{totalInstances}</strong> lab instance{totalInstances !== 1 ? 's' : ''} for{' '}
              <strong>{classData.className}</strong>.
              {assignedCount > 0 && (
                <span className="block mt-2 text-red-600">
                  Warning: {assignedCount} instance{assignedCount !== 1 ? 's are' : ' is'} currently assigned to students.
                  They will lose access to their labs.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isTerminating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleTerminateAll}
              disabled={isTerminating}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isTerminating ? 'Terminating...' : 'Terminate All'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
